"use client";

import Link from "next/link";
import { useState } from "react";

export default function AccountRecoveryForm() {
  const [idName, setIdName] = useState("");
  const [idPhone, setIdPhone] = useState("");
  const [foundUsernames, setFoundUsernames] = useState<string[] | null>(null);
  const [findIdMessage, setFindIdMessage] = useState("");
  const [findIdLoading, setFindIdLoading] = useState(false);

  const [resetEmail, setResetEmail] = useState("");
  const [resetCode, setResetCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [resetMessage, setResetMessage] = useState("");
  const [resetLoading, setResetLoading] = useState(false);
  const [resetCodeSent, setResetCodeSent] = useState(false);

  async function handleFindId(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setFindIdLoading(true);
    setFindIdMessage("");
    setFoundUsernames(null);

    try {
      const response = await fetch("/api/account/find-id", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          name: idName,
          phone: idPhone,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message ?? "아이디 찾기에 실패했습니다.");
      }

      setFoundUsernames(data.usernames ?? []);
      setFindIdMessage(
        data.usernames?.length
          ? "가입된 아이디를 찾았습니다."
          : "입력한 정보와 일치하는 계정을 찾지 못했습니다."
      );
    } catch (error) {
      setFindIdMessage(
        error instanceof Error ? error.message : "아이디 찾기 중 오류가 발생했습니다."
      );
    } finally {
      setFindIdLoading(false);
    }
  }

  async function handleSendResetCode() {
    setResetLoading(true);
    setResetMessage("");

    try {
      const response = await fetch("/api/password/send-reset-code", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          email: resetEmail,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message ?? "인증 코드 발송에 실패했습니다.");
      }

      setResetCodeSent(true);
      setResetMessage(data.message);
    } catch (error) {
      setResetMessage(
        error instanceof Error
          ? error.message
          : "인증 코드 발송 중 오류가 발생했습니다."
      );
    } finally {
      setResetLoading(false);
    }
  }

  async function handleResetPassword(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setResetLoading(true);
    setResetMessage("");

    try {
      const response = await fetch("/api/password/reset", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          email: resetEmail,
          code: resetCode,
          password: newPassword,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message ?? "비밀번호 변경에 실패했습니다.");
      }

      setResetMessage(data.message);
      setResetCode("");
      setNewPassword("");
    } catch (error) {
      setResetMessage(
        error instanceof Error
          ? error.message
          : "비밀번호 변경 중 오류가 발생했습니다."
      );
    } finally {
      setResetLoading(false);
    }
  }

  return (
    <div className="grid w-full gap-6 lg:grid-cols-2">
      <form
        onSubmit={handleFindId}
        className="rounded-[2rem] border border-white/10 bg-white/[0.04] p-6"
      >
        <p className="text-sm font-bold text-violet-300">FIND ID</p>
        <h2 className="mt-3 text-2xl font-black text-white">아이디 찾기</h2>
        <p className="mt-3 text-sm text-slate-400">
          본인인증이 완료된 계정은 이름과 인증된 휴대폰 번호로 아이디를 확인합니다.
        </p>

        <div className="mt-6">
          <label className="text-sm font-bold text-slate-300">이름</label>
          <input
            value={idName}
            onChange={(event) => setIdName(event.target.value)}
            placeholder="홍길동"
            className="mt-2 w-full rounded-2xl border border-white/10 bg-slate-950/70 px-4 py-3 text-white outline-none placeholder:text-slate-500 focus:border-violet-400"
            required
          />
        </div>

        <div className="mt-5">
          <label className="text-sm font-bold text-slate-300">휴대폰 번호</label>
          <input
            value={idPhone}
            onChange={(event) => setIdPhone(event.target.value)}
            placeholder="01012345678"
            className="mt-2 w-full rounded-2xl border border-white/10 bg-slate-950/70 px-4 py-3 text-white outline-none placeholder:text-slate-500 focus:border-violet-400"
            required
          />
        </div>

        <button
          type="submit"
          disabled={findIdLoading}
          className="mt-6 w-full rounded-2xl bg-violet-500 px-5 py-3 font-bold text-white transition hover:bg-violet-400 disabled:cursor-not-allowed disabled:opacity-60"
        >
          {findIdLoading ? "확인 중..." : "아이디 찾기"}
        </button>

        {findIdMessage && (
          <div className="mt-5 rounded-2xl border border-white/10 bg-slate-950/50 p-4 text-sm text-slate-300">
            <p>{findIdMessage}</p>
            {foundUsernames && foundUsernames.length > 0 && (
              <ul className="mt-3 space-y-2">
                {foundUsernames.map((username) => (
                  <li key={username} className="font-mono text-violet-200">
                    {username}
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </form>

      <form
        onSubmit={handleResetPassword}
        className="rounded-[2rem] border border-white/10 bg-white/[0.04] p-6"
      >
        <p className="text-sm font-bold text-violet-300">RESET PASSWORD</p>
        <h2 className="mt-3 text-2xl font-black text-white">비밀번호 재설정</h2>
        <p className="mt-3 text-sm text-slate-400">
          가입한 이메일로 인증 코드를 받은 뒤 새 비밀번호를 설정합니다.
        </p>

        <div className="mt-6">
          <label className="text-sm font-bold text-slate-300">이메일</label>
          <div className="mt-2 flex flex-col gap-3 sm:flex-row">
            <input
              type="email"
              value={resetEmail}
              onChange={(event) => setResetEmail(event.target.value)}
              placeholder="example@email.com"
              className="min-h-12 flex-1 rounded-2xl border border-white/10 bg-slate-950/70 px-4 text-white outline-none placeholder:text-slate-500 focus:border-violet-400"
              required
            />
            <button
              type="button"
              onClick={handleSendResetCode}
              disabled={resetLoading || !resetEmail}
              className="rounded-2xl border border-violet-400/30 px-4 py-3 text-sm font-bold text-violet-100 transition hover:bg-violet-500/10 disabled:cursor-not-allowed disabled:opacity-60"
            >
              코드 발송
            </button>
          </div>
        </div>

        <div className="mt-5">
          <label className="text-sm font-bold text-slate-300">인증 코드</label>
          <input
            value={resetCode}
            onChange={(event) => setResetCode(event.target.value)}
            placeholder="6자리 코드"
            maxLength={6}
            className="mt-2 w-full rounded-2xl border border-white/10 bg-slate-950/70 px-4 py-3 text-white outline-none placeholder:text-slate-500 focus:border-violet-400"
            required
          />
        </div>

        <div className="mt-5">
          <label className="text-sm font-bold text-slate-300">새 비밀번호</label>
          <input
            type="password"
            value={newPassword}
            onChange={(event) => setNewPassword(event.target.value)}
            placeholder="8자 이상"
            className="mt-2 w-full rounded-2xl border border-white/10 bg-slate-950/70 px-4 py-3 text-white outline-none placeholder:text-slate-500 focus:border-violet-400"
            required
          />
        </div>

        <button
          type="submit"
          disabled={resetLoading || !resetCodeSent}
          className="mt-6 w-full rounded-2xl bg-violet-500 px-5 py-3 font-bold text-white transition hover:bg-violet-400 disabled:cursor-not-allowed disabled:opacity-60"
        >
          {resetLoading ? "처리 중..." : "비밀번호 변경"}
        </button>

        {resetMessage && (
          <p className="mt-5 rounded-2xl border border-white/10 bg-slate-950/50 p-4 text-sm text-slate-300">
            {resetMessage}
          </p>
        )}

        <p className="mt-5 text-center text-sm text-slate-500">
          비밀번호를 변경하면 기존 로그인 세션은 모두 로그아웃됩니다.
        </p>
      </form>

      <div className="lg:col-span-2 text-center">
        <Link href="/login" className="text-sm font-bold text-violet-300 hover:underline">
          로그인으로 돌아가기
        </Link>
      </div>
    </div>
  );
}
